import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';

import { theme } from '@frontend/legacy-components/src/theme';

import { pdfStore as pdfStoreContext } from './WebViewerContext';
import { PDF_COMPARE_COLORS } from './consts';

interface PDFCompareFileUploadProps {
  instance: WebViewerInstance;
  pdfStore: typeof pdfStoreContext;
}

interface FileDisplayProps {
  fileName: string | null;
  isVisible: boolean;
  onToggleVisibility: () => void;
  onRemove?: () => void;
  color: string;
}

const FileDisplay = ({
  fileName,
  isVisible,
  onToggleVisibility,
  onRemove,
  color,
}: FileDisplayProps) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px',
        background: theme.colors.page.surface,
        borderRadius: '4px',
        border: `1px solid ${theme.colors.separator.border}`,
        width: '100%',
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: 0,
          flex: 1,
        }}
      >
        <div
          role="button"
          aria-label="Toggle visibility"
          onClick={onToggleVisibility}
          style={{
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative',
          }}
          onMouseEnter={(e) => {
            const tooltip = e.currentTarget.querySelector(
              '.tooltip',
            ) as HTMLElement;
            const triangle = e.currentTarget.querySelector(
              '.tooltip-triangle',
            ) as HTMLElement;
            if (tooltip && triangle) {
              tooltip.style.opacity = '1';
              tooltip.style.visibility = 'visible';
              triangle.style.opacity = '1';
              triangle.style.visibility = 'visible';
            }
          }}
          onMouseLeave={(e) => {
            const tooltip = e.currentTarget.querySelector(
              '.tooltip',
            ) as HTMLElement;
            const triangle = e.currentTarget.querySelector(
              '.tooltip-triangle',
            ) as HTMLElement;
            if (tooltip && triangle) {
              tooltip.style.opacity = '0';
              tooltip.style.visibility = 'hidden';
              triangle.style.opacity = '0';
              triangle.style.visibility = 'hidden';
            }
          }}
        >
          <div
            className="tooltip"
            style={{
              position: 'absolute',
              bottom: '-32px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: theme.colors.interaction.tooltip,
              color: theme.colors.feature.onInteraction,
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              opacity: 0,
              visibility: 'hidden',
              transition: 'opacity 0.2s, visibility 0.2s',
              pointerEvents: 'none',
            }}
          >
            <div
              className="tooltip-triangle"
              style={{
                position: 'absolute',
                top: '-4px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderBottom: `4px solid ${theme.colors.interaction.tooltip}`,
              }}
            />
            Visibility
          </div>
          {isVisible ? (
            <FaEye size={16} color={color} />
          ) : (
            <FaEyeSlash size={16} color={color} />
          )}
        </div>
        <span
          style={{
            fontSize: '14px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            color: theme.colors.feature.primary,
          }}
        >
          {fileName}
        </span>
      </div>
      {onRemove && (
        <div
          role="button"
          aria-label="Remove comparison file"
          onClick={onRemove}
          style={{
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative',
          }}
          onMouseEnter={(e) => {
            const tooltip = e.currentTarget.querySelector(
              '.tooltip',
            ) as HTMLElement;
            const triangle = e.currentTarget.querySelector(
              '.tooltip-triangle',
            ) as HTMLElement;
            if (tooltip && triangle) {
              tooltip.style.opacity = '1';
              tooltip.style.visibility = 'visible';
              triangle.style.opacity = '1';
              triangle.style.visibility = 'visible';
            }
          }}
          onMouseLeave={(e) => {
            const tooltip = e.currentTarget.querySelector(
              '.tooltip',
            ) as HTMLElement;
            const triangle = e.currentTarget.querySelector(
              '.tooltip-triangle',
            ) as HTMLElement;
            if (tooltip && triangle) {
              tooltip.style.opacity = '0';
              tooltip.style.visibility = 'hidden';
              triangle.style.opacity = '0';
              triangle.style.visibility = 'hidden';
            }
          }}
        >
          <div
            className="tooltip"
            style={{
              position: 'absolute',
              bottom: '-32px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: theme.colors.interaction.tooltip,
              color: theme.colors.feature.onInteraction,
              padding: '5px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              opacity: 0,
              visibility: 'hidden',
              transition: 'opacity 0.2s, visibility 0.2s',
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          >
            <div
              className="tooltip-triangle"
              style={{
                position: 'absolute',
                top: '-4px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderBottom: `4px solid ${theme.colors.interaction.tooltip}`,
              }}
            />
            Remove
          </div>
          <FaTimes size={16} color={theme.colors.feature.primary} />
        </div>
      )}
    </div>
  );
};

// Add global styles for tooltip behavior
const style = document?.createElement('style');
style.textContent = `
  .tooltip-trigger:hover + .tooltip-content,
  .tooltip-trigger:focus + .tooltip-content {
    opacity: 1 !important;
    visibility: visible !important;
  }
`;
document?.head.appendChild(style);

const PDFCompareFileUpload = ({
  instance,
  pdfStore,
}: PDFCompareFileUploadProps) => {
  // Handle unresolved promises from previous operations
  let handlingFileChangePromise: Promise<void> | null = null;

  // Helper function to refresh the compare PDF panel
  const refreshPanel = () => {
    instance.UI.disableElements(['comparePdfPanel']);
    instance.UI.enableElements(['comparePdfPanel']);
    instance.UI.setActiveLeftPanel('comparePdfPanel');
  };

  // Helper function to get array of pages from a PDF document
  const getPageArray = async (doc: Core.PDFNet.PDFDoc) => {
    const arr = [];
    const itr = await doc.getPageIterator(1);

    for (itr; await itr.hasNext(); itr.next()) {
      const page = await itr.current();
      arr.push(page);
    }

    return arr;
  };

  // Helper function to create a blank page
  const createBlankPage = async (
    pdf: Core.PDFNet.PDFDoc,
    lastPage: Core.PDFNet.Page,
  ) => {
    const rect = lastPage
      ? await lastPage.getMediaBox()
      : await new instance.Core.PDFNet.Rect(0, 0, 1224, 792);
    return pdf.pageCreate(rect);
  };

  // Helper function to run diffing and load the result
  const runAndLoadDiff = async (
    originalPDF: Core.PDFNet.PDFDoc,
    newPDF: Core.PDFNet.PDFDoc,
  ) => {
    const options = await instance.Core.PDFNet.PDFDoc.createDiffOptions();
    options.setColorA({ A: 1, R: 242, G: 169, B: 59 }); // #F2A93B
    options.setColorB({ A: 1, R: 30, G: 43, B: 158 }); // #1E2B9E

    const diffedPDF = await instance.Core.PDFNet.PDFDoc.create();
    await diffedPDF.lock();

    const [currentPDFPages, newPDFPages] = await Promise.all([
      getPageArray(originalPDF),
      getPageArray(newPDF),
    ]);

    const biggestLength = Math.max(currentPDFPages.length, newPDFPages.length);

    for (let i = 0; i < biggestLength; i++) {
      const sourcePage =
        currentPDFPages[i] ||
        (await createBlankPage(originalPDF, currentPDFPages[i - 1]));
      const comparePage =
        newPDFPages[i] || (await createBlankPage(newPDF, newPDFPages[i - 1]));
      await diffedPDF.appendVisualDiff(sourcePage, comparePage, options);
    }

    await diffedPDF.unlock();
    await instance.UI.loadDocument(diffedPDF);
    refreshPanel();
  };

  // Helper function to load a document and ensure panel update
  const loadDoc = async (doc: File | string | null) => {
    if (!doc) return;
    await instance.UI.loadDocument(doc);
    refreshPanel();
  };

  // Handle file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event?.target?.files?.[0];

    if (handlingFileChangePromise) {
      try {
        await handlingFileChangePromise;
      } catch (error) {
        console.error('Error handling previous file operation:', error);
      }
    }

    handlingFileChangePromise = (async () => {
      if (file) {
        // Set the new file and visibility states
        pdfStore.newFile = file;
        pdfStore.isOriginalFileVisible = true;
        pdfStore.isNewFileVisible = true;

        // Run diffing comparison
        const originalDoc = await instance.Core.createDocument(
          pdfStore.originalUrl || '',
        );
        const originalPDF = await originalDoc?.getPDFDoc();
        const newDoc = await instance.Core.createDocument(file);
        const newPDF = await newDoc?.getPDFDoc();

        await runAndLoadDiff(originalPDF, newPDF);
        refreshPanel();
      }
    })();

    try {
      await handlingFileChangePromise;
    } catch (error) {
      console.error('Error handling file upload:', error);
    } finally {
      handlingFileChangePromise = null;
    }
  };

  // Handle visibility toggle
  const handleToggleVisibility = async (file: 'original' | 'comparison') => {
    if (handlingFileChangePromise) {
      try {
        await handlingFileChangePromise;
      } catch (error) {
        console.error('Error handling previous visibility toggle:', error);
      }
    }

    handlingFileChangePromise = (async () => {
      if (file === 'original' && pdfStore.isOriginalFileVisible) {
        pdfStore.isOriginalFileVisible = false;
        pdfStore.isNewFileVisible = true;
        if (pdfStore.newFile) {
          await loadDoc(pdfStore.newFile);
        }
      } else if (file === 'comparison' && pdfStore.isNewFileVisible) {
        pdfStore.isOriginalFileVisible = true;
        pdfStore.isNewFileVisible = false;
        if (pdfStore.originalUrl) {
          await loadDoc(pdfStore.originalUrl);
        }
      } else {
        pdfStore.isOriginalFileVisible = true;
        pdfStore.isNewFileVisible = true;

        if (pdfStore.originalUrl && pdfStore.newFile) {
          const originalDoc = await instance.Core.createDocument(
            pdfStore.originalUrl,
          );
          const originalPDF = await originalDoc?.getPDFDoc();
          const newDoc = await instance.Core.createDocument(pdfStore.newFile);
          const newPDF = await newDoc?.getPDFDoc();

          await runAndLoadDiff(originalPDF, newPDF);
        }
      }
    })();

    try {
      await handlingFileChangePromise;
    } catch (error) {
      console.error('Error handling visibility toggle:', error);
    } finally {
      handlingFileChangePromise = null;
    }
  };

  // Handle removing comparison file
  const handleRemoveComparison = async () => {
    if (handlingFileChangePromise) {
      try {
        await handlingFileChangePromise;
      } catch (error) {
        console.error('Error handling previous file removal:', error);
      }
    }

    handlingFileChangePromise = (async () => {
      pdfStore.newFile = null;
      pdfStore.isOriginalFileVisible = true;
      pdfStore.isNewFileVisible = true;
      if (pdfStore.originalUrl) {
        await loadDoc(pdfStore.originalUrl);
      }
    })();

    try {
      await handlingFileChangePromise;
    } catch (error) {
      console.error('Error handling file removal:', error);
    } finally {
      handlingFileChangePromise = null;
    }
  };

  return (
    <div style={{ padding: '16px', width: '100%' }}>
      <div
        style={{
          fontWeight: 600,
          color: theme.colors.feature.secondary,
          textAlign: 'center',
          marginBottom: '16px',
        }}
      >
        Visually compare PDFs
      </div>

      {!pdfStore.newFile ? (
        <label
          htmlFor="file-upload"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px',
            background: theme.colors.page.surface,
            borderRadius: '4px',
            cursor: 'pointer',
            width: '100%',
          }}
        >
          <span
            style={{ fontSize: '14px', color: theme.colors.feature.primary }}
          >
            Upload a file
          </span>
          <div
            style={{
              background: PDF_COMPARE_COLORS.upload.background,
              color: theme.colors.feature.onInteraction,
              padding: '4px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            UPLOAD
          </div>
          <input
            type="file"
            id="file-upload"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            accept=".pdf"
          />
        </label>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%',
          }}
        >
          <FileDisplay
            fileName={pdfStore.filename}
            isVisible={pdfStore.isOriginalFileVisible}
            onToggleVisibility={() => handleToggleVisibility('original')}
            color={PDF_COMPARE_COLORS.original}
          />
          <FileDisplay
            fileName={pdfStore.newFile.name}
            isVisible={pdfStore.isNewFileVisible}
            onToggleVisibility={() => handleToggleVisibility('comparison')}
            onRemove={handleRemoveComparison}
            color={PDF_COMPARE_COLORS.comparison}
          />
        </div>
      )}
    </div>
  );
};

export default PDFCompareFileUpload;
